import { useMemo, useEffect, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { cloneDeep, isEqual } from 'lodash'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Grid } from '@mui/material'

import { IconThemeProvider } from 'custom-components/context/IconThemesContext'

import useDashboardQuery from '../../hooks/useDashboardQuery'
import { tileKeys } from '../../hooks/useTileQuery'
import tileApi from 'apis/disApi/tileApi'

import { isJson, logErrorMessage } from 'utils/functions/helpers'
import { SummaryMapDatarid } from './summaryMap/grid/SummaryMapDatagrid'
import { SummaryMapInfo } from './summaryMap/map/SummaryMapInfo'

export function PlottingMapComponent(props) {
	const {
		tile,
		currentDashboardKey,
		settings,
		useTileWrapper,
		tileWrapperHeight,
		gridFilterModel,
		onGridFilterModelChange,
		gridRowGroupingModel,
		onGridRowGroupingModelChange,
		gridSortModel,
		onGridSortModelChange,
		gridAggregationModel,
		onGridAggregationModelChange,
		gridColumnVisibilityModel,
		onGridColumnVisibilityModelChange,
		columnsWidth,
		onGridColumnResize,
		columnsOrder,
		onGridColumnOrderChange,
		hasSelectColumns,
	} = props

	const { environment } = useSelector((state) => state)
	const iconTheme = environment.theme.icons
	const queryClient = useQueryClient()

	// selectedRow from datagrid
	const [selectedRow, setSelectedRow] = useState(null)
	const [displayGridRows, setDisplayGridRows] = useState([])
	const [dateFilterColumn, setDateFilterColumn] = useState(null)

	const { updateMutation: updateDashboard, dashboardKeyList } = useDashboardQuery({
		dashboardKey: currentDashboardKey,
	})

	const updateTileMutation = useMutation(tileApi.update, {
		onSuccess: () => queryClient.invalidateQueries(tileKeys.allWithKey(currentDashboardKey)),
	})

	const handleSubmitSetting = async (data) => {
		try {
			const { primaryKeyList, ...restData } = data

			const isHavePrimaryKey = Boolean(primaryKeyList)
			if (isHavePrimaryKey) {
				await updateDashboard.mutateAsync({
					key: currentDashboardKey,
					data: {
						variables: JSON.stringify(primaryKeyList),
					},
					token: environment.apiToken,
				})
			}

			const editedSettings = JSON.stringify({
				...settings,
				...restData,
			})

			await updateTileMutation.mutateAsync({
				dashboardKey: currentDashboardKey,
				tileKey: tile.key,
				data: { settings: editedSettings },
				token: environment.apiToken,
			})
		} catch (error) {
			logErrorMessage(error)
		}
	}

	return (
		<IconThemeProvider values={iconTheme}>
			<Grid container sx={{ height: '100%' }}>
				<Grid item xs={4} md={4} lg={4}>
					<SummaryMapDatarid
						tile={tile}
						settings={settings}
						handleSubmitSetting={handleSubmitSetting}
						selectedRow={selectedRow}
						setSelectedRow={setSelectedRow}
						setDisplayGridRows={setDisplayGridRows}
						setDateFilterColumn={setDateFilterColumn}
						useTileWrapper={useTileWrapper}
						tileWrapperHeight={tileWrapperHeight}
						hasSelectColumns={hasSelectColumns}
						gridFilterModel={gridFilterModel}
						onGridFilterModelChange={onGridFilterModelChange}
						gridRowGroupingModel={gridRowGroupingModel}
						onGridRowGroupingModelChange={onGridRowGroupingModelChange}
						gridSortModel={gridSortModel}
						onGridSortModelChange={onGridSortModelChange}
						gridAggregationModel={gridAggregationModel}
						onGridAggregationModelChange={onGridAggregationModelChange}
						gridColumnVisibilityModel={gridColumnVisibilityModel}
						onGridColumnVisibilityModelChange={onGridColumnVisibilityModelChange}
						columnsWidth={columnsWidth}
						onGridColumnResize={onGridColumnResize}
						columnsOrder={columnsOrder}
						onGridColumnOrderChange={onGridColumnOrderChange}
					/>
				</Grid>
				<Grid item xs={8} md={8} lg={8}>
					<SummaryMapInfo
						selectedRow={selectedRow}
						settings={settings}
						handleSubmitSetting={handleSubmitSetting}
						displayGridRows={displayGridRows}
						dateFilterColumn={dateFilterColumn}
						useTileWrapper={useTileWrapper}
					/>
				</Grid>
			</Grid>
		</IconThemeProvider>
	)
}
